import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  CardMedia,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Avatar,
  ListItemAvatar,
  Divider,
  Paper,
  alpha,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { COLORS } from "colors/color";
import SvgComponent from "./SvgComponent.jsx";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import ArchiveIcon from "@mui/icons-material/Archive";
import AdminProfile from "./adminProfile.jsx";
import { useTranslation } from "react-i18next";
const apiUrl = process.env.REACT_APP_API_URL;

const StyledReturnButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '12px 24px',
  textTransform: 'none',
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: '0.95rem',
  color: COLORS.signIn,
  backgroundColor: alpha(COLORS.signIn, 0.08),
  border: `1px solid ${alpha(COLORS.signIn, 0.2)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(COLORS.signIn, 0.12),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(COLORS.signIn, 0.15)}`,
  },
}));

const StyledPostItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: 16,
  marginBottom: 20,
  padding: '24px 28px',
  border: `1px solid ${alpha(COLORS.signIn, 0.08)}`,
  boxShadow: `0 2px 12px ${alpha(COLORS.signIn, 0.04)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: `linear-gradient(135deg, ${COLORS.signIn} 0%, ${alpha(COLORS.signIn, 0.6)} 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    boxShadow: `0 8px 32px ${alpha(COLORS.signIn, 0.12)}`,
    transform: 'translateY(-4px)',
    '&::before': {
      opacity: 1,
    },
  },
}));

const StyledListContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  padding: 0,
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 40px',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  borderRadius: 20,
  border: `1px solid ${alpha(COLORS.signIn, 0.08)}`,
  boxShadow: `0 4px 24px ${alpha(COLORS.signIn, 0.06)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${COLORS.signIn} 0%, ${alpha(COLORS.signIn, 0.3)} 100%)`,
  },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: COLORS.signIn,
    '&:hover': {
      backgroundColor: alpha(COLORS.signIn, 0.08),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: COLORS.signIn,
  },
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 32,
  padding: '20px 24px',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  border: `1px solid ${alpha(COLORS.signIn, 0.08)}`,
  boxShadow: `0 4px 20px ${alpha(COLORS.signIn, 0.06)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${COLORS.signIn} 0%, ${alpha(COLORS.signIn, 0.4)} 100%)`,
  },
}));

const StatusChip = styled(Chip)(({ active }) => ({
  fontFamily: 'Poppins',
  fontWeight: 600,
  fontSize: '0.85rem',
  height: '36px',
  borderRadius: '18px',
  backgroundColor: active ? alpha(COLORS.signIn, 0.12) : alpha('#757575', 0.08),
  color: active ? COLORS.signIn : '#757575',
  border: active ? `1px solid ${alpha(COLORS.signIn, 0.2)}` : `1px solid ${alpha('#757575', 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiChip-icon': {
    color: active ? COLORS.signIn : '#757575',
    fontSize: '18px',
  },
  '&:hover': {
    backgroundColor: active ? alpha(COLORS.signIn, 0.16) : alpha('#757575', 0.12),
    transform: 'translateY(-1px)',
    boxShadow: active 
      ? `0 4px 12px ${alpha(COLORS.signIn, 0.15)}` 
      : `0 4px 12px ${alpha('#757575', 0.1)}`,
  },
}));

const RecruiterProfileCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  borderRadius: 24,
  marginBottom: 32,
  padding: '40px',
  border: `2px solid ${alpha(COLORS.signIn, 0.12)}`,
  boxShadow: `0 8px 32px ${alpha(COLORS.signIn, 0.06)}`,
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: `linear-gradient(90deg, ${COLORS.signIn} 0%, #8b5cf6 50%, ${alpha(COLORS.signIn, 0.6)} 100%)`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${alpha(COLORS.signIn, 0.05)} 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
}));

function getUserIdFromAccessToken() {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    console.error("No access token found in local storage");
    return null;
  }

  try {
    const decodedToken = parseJwt(accessToken);
    //console.log("Access token : ", decodedToken);
    return decodedToken.user_id;
  } catch (error) {
    console.error("Failed to decode access token", error);
    return null;
  }
}

function parseJwt(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );
  //console.log("payload : ", jsonPayload);

  return JSON.parse(jsonPayload);
}

function RecruiterDetails({ recruiterId, onBack }) {
  // Destructure onBack here
  const [recruiter, setRecruiter] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsArchived, setPostsArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const adminId = getUserIdFromAccessToken();
  const [postFilter, setPostFilter] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    if (adminId) {
      fetchRecruiterDetails();
      fetchRecruiterPosts();
      fetchRecruiterPostsArchived();
    }
  }, [recruiterId, adminId]);

  const fetchRecruiterDetails = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}recruiters/admin/${adminId}/recruiters/${recruiterId}/`
      );
      setRecruiter(response.data);
      console.log("data 212: ", response.data);
    } catch (error) {
      console.error("Failed to fetch recruiter details", error);
    }
  };

  const fetchRecruiterPosts = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}recruiters/${recruiterId}/posts/`
      );
      setPosts(response.data);
      console.log("data 2124: ", response.data);
    } catch (error) {
      console.error("Failed to fetch recruiter posts", error);
    }
  };
  const fetchRecruiterPostsArchived = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}recruiters/${recruiterId}/posts/archived/`
      );
      setPostsArchived(response.data);
      console.log("data archived: ", response.data);
    } catch (error) {
      console.error("Failed to fetch recruiter archived posts", error);
    }
  };

  // Get current posts to display based on toggle
  const currentPosts = showArchived ? postsArchived : posts;
  const currentPostsCount = currentPosts.length;

  // Handle toggle change
  const handleToggleChange = (event) => {
    setShowArchived(event.target.checked);
  };

  if (!recruiter) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box 
          sx={{
            width: 60,
            height: 60,
            border: `4px solid ${alpha(COLORS.signIn, 0.2)}`,
            borderTop: `4px solid ${COLORS.signIn}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography
          sx={{
            color: alpha(COLORS.signIn, 0.7),
            fontFamily: 'Poppins',
            fontWeight: 500,
          }}
        >
          {t('recruiterDetails.loadingMessage')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      padding: 3, 
      backgroundColor: '#fefefe',
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #fefefe 0%, #f8fafc 100%)',
    }}>
      {/* Enhanced Return Button */}
      <Box sx={{ mb: 4 }}>
        <StyledReturnButton
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
        >
          {t('recruiterDetails.backToRecruiters')}
        </StyledReturnButton>
      </Box>

      {/* Combined Recruiter Profile Card with Statistics */}
      <RecruiterProfileCard>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={recruiter.image}
              sx={{
                width: 100,
                height: 100,
                backgroundColor: alpha(COLORS.signIn, 0.15),
                color: COLORS.signIn,
                border: `6px solid rgba(255, 255, 255, 0.8)`,
                boxShadow: `0 8px 32px ${alpha(COLORS.signIn, 0.2)}`,
                fontSize: '2.5rem',
                fontWeight: 700,
                fontFamily: 'Poppins',
              }}
            >
              {!recruiter.image && `${recruiter.first_name[0]}${recruiter.last_name[0]}`}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: '#10b981',
                border: '4px solid rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                }}
              />
            </Box>
          </Box>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Header with Title */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h3"
                sx={{
                  color: '#1e293b',
                  fontFamily: 'Poppins',
                  fontWeight: 800,
                  mb: 1,
                  fontSize: '2rem',
                  letterSpacing: '-0.025em',
                }}
              >
                {recruiter.first_name} {recruiter.last_name}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#64748b',
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  fontSize: '1.1rem',
                  mb: 2,
                }}
              >
                {t('recruiterDetails.profileDashboardTitle')}
              </Typography>
            </Box>
            
   
            
            {/* Company & Location Info */}
            {(recruiter.company || recruiter.city || recruiter.country) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                {recruiter.company && (
                  <Chip
                    icon={<BusinessIcon />}
                    label={recruiter.company}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: COLORS.signIn,
                      fontFamily: 'Poppins',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      height: '36px',
                      borderRadius: '18px',
                      border: `2px solid ${alpha(COLORS.signIn, 0.15)}`,
                      boxShadow: `0 2px 8px ${alpha(COLORS.signIn, 0.1)}`,
                      '& .MuiChip-icon': {
                        color: COLORS.signIn,
                        fontSize: '18px',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(COLORS.signIn, 0.15)}`,
                      },
                    }}
                  />
                )}
                {(recruiter.city || recruiter.country) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      padding: '8px 16px',
                      borderRadius: '16px',
                      border: `1px solid ${alpha('#64748b', 0.15)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#64748b',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}
                    >
                      {[recruiter.city, recruiter.country].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Enhanced Statistics Section */}
            <Box 
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 3,
                mt: 2,
              }}
            >
              {/* Total Posts Statistics */}
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '16px',
                  border: `2px solid ${alpha(COLORS.signIn, 0.1)}`,
                  boxShadow: `0 4px 16px ${alpha(COLORS.signIn, 0.08)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(COLORS.signIn, 0.12)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: alpha(COLORS.signIn, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WorkOutlineIcon sx={{ color: COLORS.signIn, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: COLORS.signIn,
                        fontFamily: 'Poppins',
                        fontWeight: 800,
                        fontSize: '1.8rem',
                        lineHeight: 1,
                      }}
                    >
                      {posts.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {t('recruiterDetails.activePostsLabel')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Archived Posts Statistics */}
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '16px',
                  border: `2px solid ${alpha('#757575', 0.1)}`,
                  boxShadow: `0 4px 16px ${alpha('#757575', 0.08)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha('#757575', 0.12)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: alpha('#757575', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArchiveIcon sx={{ color: '#757575', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#757575',
                        fontFamily: 'Poppins',
                        fontWeight: 800,
                        fontSize: '1.8rem',
                        lineHeight: 1,
                      }}
                    >
                      {postsArchived.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {t('recruiterDetails.archivedPostsLabel')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Total Applications Statistics */}
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '16px',
                  border: `2px solid ${alpha('#10b981', 0.1)}`,
                  boxShadow: `0 4px 16px ${alpha('#10b981', 0.08)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha('#10b981', 0.12)}`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: alpha('#10b981', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PeopleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#10b981',
                        fontFamily: 'Poppins',
                        fontWeight: 800,
                        fontSize: '1.8rem',
                        lineHeight: 1,
                      }}
                    >
                      {posts.reduce((total, post) => total + (post.total_applications || 0), 0) + 
                       postsArchived.reduce((total, post) => total + (post.total_applications || 0), 0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {t('recruiterDetails.totalApplicationsLabel')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </RecruiterProfileCard>

      {/* Toggle Switch Section */}
      <ToggleContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StatusChip
            icon={<WorkOutlineIcon />}
            label={t('recruiterDetails.activePosts')}
            active={!showArchived}
            variant={!showArchived ? "filled" : "outlined"}
          />
          <StatusChip
            icon={<ArchiveIcon />}
            label={t('recruiterDetails.archivedPosts')}
            active={showArchived}
            variant={showArchived ? "filled" : "outlined"}
          />
        </Box>
        <FormControlLabel
          control={
            <StyledSwitch
              checked={showArchived}
              onChange={handleToggleChange}
              name="showArchived"
            />
          }
          label={
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: COLORS.signIn,
                fontSize: '0.9rem',
              }}
            >
              {showArchived 
                ? t('recruiterDetails.showArchivedOnly') 
                : t('recruiterDetails.showActiveOnly')
              }
            </Typography>
          }
          labelPlacement="start"
        />
      </ToggleContainer>

      {/* Posts List or Empty State */}
      {currentPostsCount === 0 ? (
        <EmptyStateContainer>
          {showArchived ? (
            <ArchiveIcon
              sx={{
                fontSize: 64,
                color: alpha(COLORS.signIn, 0.3),
                mb: 2,
              }}
            />
          ) : (
            <WorkOutlineIcon
              sx={{
                fontSize: 64,
                color: alpha(COLORS.signIn, 0.3),
                mb: 2,
              }}
            />
          )}
          <Typography
            variant="h6"
            sx={{
              color: alpha(COLORS.signIn, 0.7),
              fontFamily: 'Poppins',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {showArchived 
              ? t('recruiterDetails.noArchivedPostsYet') 
              : t('recruiterDetails.noPostsYet')
            }
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: alpha(COLORS.signIn, 0.5),
              fontFamily: 'Poppins',
              maxWidth: 300,
            }}
          >
            {showArchived 
              ? t('recruiterDetails.noArchivedPostsMessage') 
              : t('recruiterDetails.noPostsMessage')
            }
          </Typography>
        </EmptyStateContainer>
      ) : (
        <StyledListContainer>
          <List sx={{ padding: 0 }}>
            {currentPosts.map((post, index) => (
              <StyledPostItem key={post.id} alignItems="flex-start">
                <ListItemAvatar>
                  <Avatar
                    src={post.image || recruiter.image}
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: alpha(COLORS.signIn, 0.1),
                      color: COLORS.signIn,
                      marginRight: 3,
                      border: `3px solid ${alpha(COLORS.signIn, 0.1)}`,
                      boxShadow: `0 4px 16px ${alpha(COLORS.signIn, 0.15)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 6px 20px ${alpha(COLORS.signIn, 0.2)}`,
                      },
                    }}
                  >
                    {!post.image && !recruiter.image && (
                      <BusinessIcon sx={{ fontSize: 28 }} />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: COLORS.signIn,
                          fontFamily: 'Poppins',
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {post.title}
                      </Typography>
                      {showArchived && (
                        <Chip
                          icon={<ArchiveIcon />}
                          label={t('general.archived')}
                          size="small"
                          sx={{
                            backgroundColor: alpha('#757575', 0.12),
                            color: '#757575',
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                            borderRadius: '12px',
                            border: `1px solid ${alpha('#757575', 0.2)}`,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          mb: 1.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.75,
                          backgroundColor: alpha(COLORS.signIn, 0.05),
                          padding: '8px 12px',
                          borderRadius: '20px',
                          border: `1px solid ${alpha(COLORS.signIn, 0.1)}`,
                        }}>
                          <CalendarTodayIcon
                            sx={{
                              fontSize: 16,
                              color: COLORS.signIn,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: COLORS.signIn,
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {t('recruiterDetails.deadline')}: {post.deadline || t('recruiterDetails.notSpecified')}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.75,
                          backgroundColor: alpha('#10b981', 0.05),
                          padding: '8px 12px',
                          borderRadius: '20px',
                          border: `1px solid ${alpha('#10b981', 0.1)}`,
                        }}>
                          <PeopleIcon
                            sx={{
                              fontSize: 16,
                              color: '#10b981',
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#10b981',
                              fontFamily: 'Poppins',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {t('recruiterDetails.applications', { count: post.total_applications || 0 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                />
              </StyledPostItem>
            ))}
          </List>
        </StyledListContainer>
      )}
    </Box>
  );
}

export default RecruiterDetails;
